<template>
  <div class="login-page">
    <div class="login-card">
      <div class="login-card__brand">
        <div class="login-card__logo">🔗</div>
        <h1 class="login-card__title">Sign In</h1>
        <p class="login-card__subtitle">Access your account</p>
      </div>

      <ReusableForm id="supplier_login_form" :loading="submitting" submit-label="Sign In" loading-label="Signing in..."
        :show-cancel="false" @submit.prevent="handleSubmit">
        <div v-if="topError" class="rform__error login-card__top-error">{{ topError }}</div>

        <div class="rform__field">
          <label class="rform__label rform__label--required">Email</label>
          <input v-model="form.email" type="email" id="email" name="email" ref="email" class="rform__input"
            :class="{ 'is-invalid': errors.email }" placeholder="you@company.com" autocomplete="username" />
          <span v-if="errors.email" class="rform__error">{{ errors.email }}</span>
        </div>

        <div class="rform__field">
          <label class="rform__label rform__label--required">Password</label>
          <div class="login-card__password-wrap">
            <input :type="showPassword ? 'text' : 'password'" v-model="form.password" id="password" name="password"
              ref="password" class="rform__input" :class="{ 'is-invalid': errors.password }"
              placeholder="Enter your password" autocomplete="current-password" />
            <button type="button" class="login-card__toggle-visibility"
              :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
              {{ showPassword ? '🙈' : '👁' }}
            </button>
          </div>
          <span v-if="errors.password" class="rform__error">{{ errors.password }}</span>
        </div>
      </ReusableForm>

      <p class="login-card__signup">
        Don't have an account?
        <router-link :to="{ name: 'Signup' }" class="login-card__signup-link">Sign up</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import ReusableForm from '@/components/utils/Form.vue'
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'SupplierLogin',
  components: { ReusableForm },

  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      errors: {},
      topError: '',
      submitting: false,
      showPassword: false,
    }
  },

  methods: {
    async handleSubmit() {
      this.errors = {}
      this.topError = ''
      this.submitting = true

      const result = await form_api_submit({
        url: 'supplier/login',
        method: 'POST',
        data: this.form,
        curr_form_id: '#supplier_login_form',
        show_success_toast: true,
        show_error_toast: false,
        remove_pre_toasts: true,
      })

      if (result.success) {
        const { token } = result.data || {}
        if (token) localStorage.setItem('supplier_token', token)
        localStorage.setItem('supplier_id', result.data.supplier.id);
        localStorage.setItem('company_id', result.data.company_id);
        this.isLoggedIn = true;
        // Adjust to wherever a signed-in supplier should land.
        this.$router.push({
          name: 'Profile',
        });
      } else if (result?.data?.error_type === 'invalid_credentials') {
        this.topError = result.message || 'Invalid credentials.'
      } else if (result?.data?.error_type === 'validation' && result.data.errors) {
        this.errors = result.data.errors
      } else {
        this.topError = result?.message || 'Something went wrong. Please try again.'
      }

      this.submitting = false
    },
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(180deg, var(--color-tint-light) 0%, var(--color-surface) 60%);
}

.login-card {
  width: 100%;
  max-width: 600px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  padding: 32px 28px;
  box-shadow: 0 24px 50px -28px rgba(12, 18, 83, 0.35);
}

.login-card__brand {
  text-align: center;
  margin-bottom: 26px;
}

.login-card__logo {
  width: 44px;
  height: 44px;
  margin: 0 auto 12px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.login-card__title {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 800;
  color: var(--color-text-dark);
}

.login-card__subtitle {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.login-card__top-error {
  margin-bottom: 4px;
  padding: 8px 10px;
  background: rgba(179, 38, 30, 0.06);
  border: 1px solid rgba(179, 38, 30, 0.2);
}

.login-card__password-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.login-card__password-wrap .rform__input {
  padding-right: 40px;
}

.login-card__toggle-visibility {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 15px;
  color: var(--color-text-muted);
  width: 28px;
  height: 28px;
}

.login-card__toggle-visibility:hover {
  color: var(--color-text-dark);
}

.login-card__signup {
  margin: 18px 0 0;
  text-align: center;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.login-card__signup-link {
  color: var(--color-secondary);
  font-weight: 700;
  text-decoration: none;
}

.login-card__signup-link:hover {
  text-decoration: underline;
}
</style>